import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from '@/i18n';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import type { BulkActionItem } from '@/components/ui/tables/Table.types';
import type { DateRangeValue } from '@/components/ui/filters/DateRangeFilter';
import { shiftAssignmentService } from '@/api/domains/shiftAssignment';
import type { ShiftAssignmentListItem } from '@/api/domains/shiftAssignment';
import { shiftService } from '@/api/domains/shift';
import type { CompanyShiftCatalogItem } from '@/api/domains/shift';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { ROUTE_DEFINITIONS } from '@/routes';


/** Se trae el catálogo completo de turnos en una sola llamada para poblar el filtro. */
const SHIFT_CATALOG_PAGE_SIZE = 1000;

type ShiftAssignmentFilters = {
  shiftId: string;
  status: string;
  dateFrom?: string;
  dateTo?: string;
};

export interface UseShiftAssignmentListResult {
  rows: ShiftAssignmentListItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<ShiftAssignmentListItem>[];
  actions: TableAction<ShiftAssignmentListItem>[];
  bulkActions: BulkActionItem[];
  onSearch: (term: string) => void;
  onEnterSearch: () => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  isLoading: boolean;
  filters: ShiftAssignmentFilters;
  onFilterChange: (key: string, value: string | string[] | undefined) => void;
  dateRange: DateRangeValue;
  onDateRangeChange: (range: DateRangeValue) => void;
  shiftOptions: { value: string; label: string }[];
  statusOptions: { value: string; label: string }[];
  emptyMessage: string;
  comingSoonTooltip: string;
}

export function useShiftAssignmentList(): UseShiftAssignmentListResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();

  const [allShifts, setAllShifts] = useState<CompanyShiftCatalogItem[]>([]);

  // Catálogo de turnos para el filtro. El filtro envía el idTimturno, nunca el nombre.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const response = await shiftService.getCompanyShifts({ pageSize: SHIFT_CATALOG_PAGE_SIZE });
      if (cancelled) return;
      if (response.success && response.data) {
        setAllShifts(response.data.items);
      } else if (response.error?.code !== 'ABORTED') {
        showApiError(response, translate('schedule:shiftAssignment.messages.loadFiltersError'));
      }
    })();
    return () => { cancelled = true; };
  }, [showApiError, translate]);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<ShiftAssignmentFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<ShiftAssignmentListItem> | null> => {
      const response = await shiftAssignmentService.getShiftAssignments(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          orderBy: params.sort?.key,
          // `PaginatedListSort.direction` ya viene normalizada a 'ASC' | 'DESC'.
          orderDirection: params.sort?.direction,
          dateFrom: params.filters.dateFrom,
          dateTo: params.filters.dateTo,
          shiftId: params.filters.shiftId ? Number(params.filters.shiftId) : undefined,
          status: params.filters.status || undefined,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code !== 'ABORTED') {
          showApiError(response, translate('schedule:shiftAssignment.messages.loadError'));
        }
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading: isLoading,
    onSearch,
    onSortChange,
    flushSearch,
    filters,
    setFilters,
  } = usePaginatedList<ShiftAssignmentListItem, ShiftAssignmentFilters>({
    fetchFn,
    // El rango de fechas arranca vacío a propósito: es un histórico, y precargar un rango
    // por defecto ocultaría la mayor parte de los registros en la primera carga.
    initialFilters: { shiftId: '', status: '', dateFrom: undefined, dateTo: undefined },
    // Criterio de aceptación: ordenado por fecha de asignación descendente por defecto.
    initialSort: { key: 'assignedAt', direction: 'DESC' },
    orderByMap: {
      employeeTaxIdentificationNumber: 'employeeTaxIdentificationNumber',
      employeeName: 'employeeName',
      shiftName: 'shiftName',
      startDate: 'startDate',
      endDate: 'endDate',
      assignedAt: 'assignedAt',
      assignedBy: 'assignedBy',
      status: 'status',
    },
    screenCode: ROUTE_DEFINITIONS.ASIGNACIONES_TURNOS.code,
  });

  const onFilterChange = useCallback(
    (key: string, value: string | string[] | undefined) => {
      const newValue = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
      setFilters((prev) => ({ ...prev, [key]: newValue }));
    },
    [setFilters],
  );

  const dateRange: DateRangeValue = useMemo(
    () => ({ from: filters.dateFrom, to: filters.dateTo }),
    [filters.dateFrom, filters.dateTo],
  );

  const onDateRangeChange = useCallback(
    (range: DateRangeValue) => {
      setFilters((prev) => ({ ...prev, dateFrom: range.from, dateTo: range.to }));
    },
    [setFilters],
  );

  const allOption = useMemo(
    () => ({ value: '', label: translate('schedule:shiftAssignment.filters.all') }),
    [translate],
  );

  const shiftOptions = useMemo(
    () => [allOption, ...allShifts.map((s) => ({ value: String(s.id), label: s.shiftName }))],
    [allOption, allShifts],
  );

  const statusOptions = useMemo(
    () => [
      allOption,
      { value: 'Activo', label: translate('schedule:shiftAssignment.filters.active') },
      { value: 'Finalizado', label: translate('schedule:shiftAssignment.filters.finished') },
    ],
    [allOption, translate],
  );

  const indefiniteLabel = translate('schedule:shiftAssignment.values.indefinite');

  const columns: TableColumn<ShiftAssignmentListItem>[] = useMemo(
    () => [
      {
        key: 'employeeTaxIdentificationNumber',
        header: translate('schedule:shiftAssignment.columns.employeeTaxIdentificationNumber'),
        sortable: true,
        render: (row: ShiftAssignmentListItem) => row.employeeTaxIdentificationNumber || '-',
      },
      {
        key: 'employeeName',
        header: translate('schedule:shiftAssignment.columns.employeeName'),
        sortable: true,
        render: (row: ShiftAssignmentListItem) => row.employeeName || '-',
      },
      {
        key: 'shiftName',
        header: translate('schedule:shiftAssignment.columns.shiftName'),
        sortable: true,
        render: (row: ShiftAssignmentListItem) => row.shiftName || '-',
      },
      {
        key: 'startDate',
        header: translate('schedule:shiftAssignment.columns.startDate'),
        sortable: true,
        render: (row: ShiftAssignmentListItem) => row.startDate || '-',
      },
      {
        key: 'endDate',
        header: translate('schedule:shiftAssignment.columns.endDate'),
        sortable: true,
        // Una asignación sin fecha de término se muestra como "Indefinido" (diseño ASIS-5656),
        // no como guion: el guion se reserva para un dato ausente.
        render: (row: ShiftAssignmentListItem) => row.endDate || indefiniteLabel,
      },
      {
        key: 'assignedAt',
        header: translate('schedule:shiftAssignment.columns.assignedAt'),
        sortable: true,
        render: (row: ShiftAssignmentListItem) => row.assignedAt || '-',
      },
      {
        key: 'assignedBy',
        header: translate('schedule:shiftAssignment.columns.assignedBy'),
        sortable: true,
        render: (row: ShiftAssignmentListItem) => row.assignedBy || '-',
      },
      {
        key: 'status',
        header: translate('schedule:shiftAssignment.columns.status'),
        sortable: true,
        render: (row: ShiftAssignmentListItem) => row.status || '-',
      },
    ],
    [translate, indefiniteLabel],
  );

  const comingSoonTooltip = translate('common:messages.featureNotAvailable');

  // "Detalle" pertenece a ASIS-5658. Se deja visible y deshabilitada a propósito: así la fila
  // no cambia de forma cuando esa historia se implemente, y el tooltip explica por qué no
  // está disponible en vez de dejar una opción muerta sin motivo.
  const actions: TableAction<ShiftAssignmentListItem>[] = useMemo(
    () => [
      {
        id: 'detail',
        label: translate('common:tableActions.detail'),
        icon: 'eye',
        onClick: () => undefined,
        disabled: () => true,
        tooltip: comingSoonTooltip,
      },
    ],
    [translate, comingSoonTooltip],
  );

  // Edición masiva: ASIS-5660. Mismo criterio que las acciones de fila.
  const bulkActions: BulkActionItem[] = useMemo(
    () => [
      {
        label: translate('common:actions.edit'),
        icon: 'edit',
        onClick: () => undefined,
        disabled: true,
        tooltip: comingSoonTooltip,
      },
    ],
    [translate, comingSoonTooltip],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    bulkActions,
    onSearch,
    onEnterSearch: flushSearch,
    onSortChange,
    isLoading,
    filters,
    onFilterChange,
    dateRange,
    onDateRangeChange,
    shiftOptions,
    statusOptions,
    emptyMessage: translate('schedule:shiftAssignment.messages.empty'),
    comingSoonTooltip,
  };
}
